import { t } from '@/helpers/i18n';

// The silhouette is what the user sees in the very frame of the click, long before the vue
// chunk and the modal chunk arrive. It is not the modal's chrome — the chrome is rendered by
// `vue/components/modal` once it is there — it only has to read as "a modal is opening", so it
// approximates the shape of the modal it stands in for and is thrown away on the handover.
//
// Because it must be there synchronously, it lives in the main pack and can never be imported
// from a modal's own chunk: it would then arrive exactly as late as the thing it covers for.
//
// A preset is named after the modal it stands in for, and its rows copy that modal's rows as
// measured live on the desktop: same heights, same rhythm, a bar per line of text. Where the
// two brands render different rows (an extra phone row, a second consent checkbox), the preset
// branches on the brand. The sizes live in `stylesheets/blocks/b-fbl-loader.sass`.
const kladovkinOnly = markup => (window.IS_BRAND_RU ? markup : '');

const PRESETS = {
  // a centered title and note, name, country + phone, a comment textarea, consents, a submit
  call_lead: () => `
    <div class='head is-centered'>
      <div class='title'></div>
      <div class='note'></div>
    </div>
    <div class='body'>
      <div class='field'></div>
      <div class='split'>
        <div class='field is-country'></div>
        <div class='field'></div>
      </div>
      <div class='field is-textarea'></div>
      <div class='checkbox'></div>
      ${kladovkinOnly('<div class=\'checkbox\'></div>')}
      <div class='submit'></div>
    </div>
    <div class='note-lines is-small'><div></div><div></div></div>
  `,
  // a title, a two-line note and a stack of contact rows, messengers side by side at the bottom
  contacts: () => `
    <div class='head'>
      <div class='title'></div>
      <div class='note-lines'><div></div><div></div></div>
    </div>
    <div class='body'>
      <div class='row'></div>
      ${kladovkinOnly('<div class=\'row\'></div>')}
      <div class='row'></div>
      <div class='split'>
        <div class='row'></div>
        <div class='row'></div>
      </div>
    </div>
    <div class='note-lines is-small'><div></div><div></div></div>
  `,
  // a centered title over the location and language selects, cancel and confirm underneath
  locale_switcher: () => `
    <div class='title is-centered'></div>
    <div class='body'>
      <div class='field'></div>
      <div class='field'></div>
    </div>
    <div class='buttons'><div></div><div></div></div>
  `,
  // the sign-up tab, opened by the rent flow: a two-line title, the tabs, email, password,
  // phone, consents and a submit
  auth_sign_up: () => `
    <div class='head'>
      <div class='title-lines'><div></div><div></div></div>
      <div class='split tabs'><div></div><div></div></div>
    </div>
    <div class='body'>
      <div class='field'></div>
      <div class='field'></div>
      <div class='split'>
        <div class='field is-country'></div>
        <div class='field'></div>
      </div>
    </div>
    <div class='consents'>
      <div class='checkbox'></div>
      ${kladovkinOnly('<div class=\'checkbox\'></div>')}
    </div>
    <div class='submit'></div>
  `,
  // the log-in tab, opened from the top menu: email, password, the forgot-password link,
  // a submit and the phone-login button behind a separator
  auth_sign_in: () => `
    <div class='head'>
      <div class='title'></div>
      <div class='split tabs'><div></div><div></div></div>
    </div>
    <div class='body'>
      <div class='field'></div>
      <div class='field'></div>
      <div class='link'></div>
      <div class='submit'></div>
    </div>
    <div class='separator'></div>
    <div class='submit'></div>
  `,
  // for a signed-in client, whose phone the server already knows: a two-line headline over
  // a paragraph and a single button
  guest_visit_signed_in: () => `
    <div class='title-lines'><div></div><div></div></div>
    <div class='note-lines is-small'><div></div><div></div><div></div><div></div></div>
    <div class='submit'></div>
  `,
  // for a visitor wo an account the same modal opens as a form: name, country + phone,
  // consents and a submit under the headline and the paragraph
  guest_visit_signed_out: () => `
    <div class='title-lines'><div></div><div></div></div>
    <div class='note-lines is-small'><div></div><div></div><div></div><div></div></div>
    <div class='field'></div>
    <div class='split'>
      <div class='field is-country'></div>
      <div class='field'></div>
    </div>
    <div class='checkbox'></div>
    ${kladovkinOnly('<div class=\'checkbox\'></div>')}
    <div class='submit'></div>
  `
};

const PRESET_NAMES = Object.keys(PRESETS);

export default function silhouetteMarkup({ preset, cssClass }) {
  const body = PRESETS[preset];

  if (!body) {
    throw new Error(`unknown modal silhouette '${preset}', expected one of ${PRESET_NAMES}`);
  }

  return `
    <div class="b-modal v4${cssClass ? ' ' + cssClass : ''}">
      <div class="modal-shade"></div>
      <div class="modal-inner">
        <button class="modal-close" aria-label="${t('close')}"></button>
        <section class='b-fbl-loader'>
          <div class='item is-${preset.replace(/_/g, '-')}'>${body()}</div>
        </section>
      </div>
    </div>
  `;
}
